// Layer 3 (Transformation): polish.P9 (#657) — the Mass balance tab, B's
// ledger in the locked comp (`metal-accounting.html`, `ledgerB`): Paul's five
// mass-balance rows (`massBalanceRows`), with recovery (actual · design ·
// target · variance against target, `recoveryPerformanceRows`) and the loss to
// tails (`tailsLossRead`) folded in as columns, so the separate recovery table
// goes. Every cell is one of those reads, re-laid; nothing is re-derived.
// Pure — no React.

import type { VizTone } from '@/versions/v1/modules/_viz';
import type { SamplePoint } from '@/versions/v1/schema/unit-operation';
import { adherenceTone } from '@/versions/v1/modules/ingestion/ProcessGraph.transform';
import {
  massBalanceRows,
  recoveryUncertainty,
  tailsLossRead,
  type MassBalanceCheck,
  type PeriodView,
} from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import { recoveryPerformanceRows, type RecoveryPerformanceRow } from './RecoveryPerformanceTable.transform';

export type LedgerRun = { text: string; tone?: VizTone };

export type LedgerRecovery = {
  element: 'Pb' | 'Zn' | 'Ag';
  actual: string;
  design: string;
  target: string;
  variance: string;
  varianceTone: VizTone;
  /** The ± from assay precision (paul-tarp.A16), for the entry's tooltip. */
  uncertainty: string | null;
};

export type LedgerRow = {
  id: string;
  stream: string;
  meta: readonly LedgerRun[];
  indent: boolean;
  total: boolean;
  tonnes: string;
  grades: readonly { id: 'pb' | 'zn' | 'ag'; text: string; tone?: VizTone }[];
  recovery: readonly LedgerRecovery[];
  toTails: { lead: string; rest: string } | null;
  check: MassBalanceCheck;
  title: string;
};

const ROW_META: Readonly<Record<string, string>> = {
  'mb-feed': 'WIT-021 metered · head back-calculated',
  'mb-pbcon': 'SA03 sampled',
  'mb-zncon': 'SA04 sampled',
  'mb-tails': 'SA05 sampled',
};

const ROW_RECOVERY: Readonly<Record<string, readonly ('Pb' | 'Zn' | 'Ag')[]>> = {
  'mb-pbcon': ['Pb', 'Ag'],
  'mb-zncon': ['Zn'],
};

function recovery(row: RecoveryPerformanceRow, view: PeriodView): LedgerRecovery {
  return {
    uncertainty: recoveryUncertainty(row.element, view)?.tooltip ?? null,
    element: row.element,
    actual: row.actual,
    design: row.design,
    target: row.target,
    variance: row.variance,
    varianceTone: row.tone,
  };
}

function toTails(view: PeriodView): LedgerRow['toTails'] {
  const reads = (['Zn', 'Pb', 'Ag'] as const).map((element) => tailsLossRead(element, view));
  const lead = reads.find((read) => read.tailsAssayTone === 'bad') ?? reads[0]!;
  if (lead.lossPct === null) return null;
  const pct = (value: number | null) => (value === null ? '—' : `${value.toFixed(1)}%`);
  return {
    lead: `${lead.element} ${pct(lead.lossPct)}`,
    rest: reads
      .filter((read) => read !== lead)
      .map((read) => `${read.element} ${pct(read.lossPct)}`)
      .join(' · '),
  };
}

/** The ledger's five rows for the selected period (`—` throughout at shift). */
export function massBalanceLedger(view: PeriodView, samplePoints: readonly SamplePoint[]): readonly LedgerRow[] {
  const recoveries = recoveryPerformanceRows(view);
  const tails = toTails(view);
  const sa05 = samplePoints.find((row) => row.id === 'SA05');
  const tailsAssayTone = (element: 'Pb' | 'Zn' | 'Ag'): VizTone | undefined =>
    tailsLossRead(element, view).tailsAssayTone === 'bad' ? 'bad' : undefined;

  return massBalanceRows(view).map((row) => {
    const meta: LedgerRun[] = ROW_META[row.id] ? [{ text: ROW_META[row.id]! }] : [];
    if (row.id === 'mb-tails' && sa05 && sa05.adherence !== 'REG') {
      meta.push({ text: ' · ' }, { text: sa05.adherence.toLowerCase(), tone: adherenceTone(sa05.adherence) });
    }
    const isTails = row.id === 'mb-tails';
    return {
      id: row.id,
      stream: row.stream,
      meta,
      indent: row.indent,
      total: row.id === 'mb-closure',
      tonnes: row.tonnes,
      grades: [
        { id: 'pb', text: row.pbPct, tone: isTails ? tailsAssayTone('Pb') : undefined },
        { id: 'zn', text: row.znPct, tone: isTails ? tailsAssayTone('Zn') : undefined },
        { id: 'ag', text: row.agOzPerT, tone: isTails ? tailsAssayTone('Ag') : undefined },
      ],
      recovery: (ROW_RECOVERY[row.id] ?? [])
        .map((element) => recoveries.find((entry) => entry.element === element))
        .filter((entry): entry is RecoveryPerformanceRow => entry !== undefined && entry.actual !== '—')
        .map((entry) => recovery(entry, view)),
      toTails: isTails ? tails : null,
      check: row.check,
      title: row.title,
    };
  });
}

/** Tab count: the streams the ledger carries (the closure row is their sum,
 *  not a stream). */
export function ledgerStreamCount(view: PeriodView): string {
  const streams = massBalanceRows(view).filter((row) => row.id !== 'mb-closure').length;
  return `${streams} streams`;
}
